/**
 * WEB-2058 — ИСТОРИЯ ОТМЕНЫ (WEB-624), вынесена из хранилища редактора
 * отдельным модулем: потолок стеков, обёртки редьюсеров «положить ход в
 * прошлое, если содержимое сменилось» и сами шаги отмены/повтора. Перенесено
 * дословно; таблица действий хранилища зовёт `undoState`/`redoState`.
 */
import type { PayloadAction } from '@reduxjs/toolkit';
import type { ContentSnapshot, EditorState } from '../editorSlice';

/** WEB-624: max entries retained per history stack; oldest dropped past this. */
export const HISTORY_LIMIT = 50;

/**
 * WEB-624: higher-order wrapper for content-mutating case reducers.
 *
 * Wraps a reducer so that, whenever it actually changes `content` or `logic`,
 * the PRE-action `{content, logic}` snapshot is pushed onto `past` (capped at
 * HISTORY_LIMIT) and `future` is cleared (a fresh edit branches history —
 * redo of the old future no longer applies). Reducers that leave content/logic
 * unchanged (e.g. a no-op) do not push a wasted history entry. Only reducers
 * that mutate the undoable payload should be wrapped; selection/ui-only
 * reducers (selectScreen, setName, ...) are left unwrapped so they are
 * correctly excluded from undo/redo per the WEB-624 design.
 *
 * ПОПРАВКА ФАКТА (ревизия пачки 37, находка Э3). Здесь стояло «setThemeId
 * намеренно НЕ обёрнут — по замыслу WEB-624». С WEB-1239 это неправда:
 * оформление вошло в отменяемое состояние, и `setThemeId` в `editorSlice`
 * обёрнут `withHistoryPayload` наравне с восемью другими редьюсерами
 * оформления. Докблок, обещающий обратное, опаснее отсутствующего: следующий
 * читатель снял бы обёртку «по замыслу» и вернул бы дефект WEB-1239 —
 * подобранная тема не возвращалась «Отменить действие» ничем.
 */
/**
 * WEB-1239: ОФОРМЛЕНИЕ СРАВНИВАЕТСЯ ПО ЗНАЧЕНИЮ, а не по ссылке.
 *
 * Содержимое и логика меняются целыми кусками, и ссылочного сравнения им
 * хватает. Редьюсеры оформления устроены иначе: каждый из них пересобирает
 * `settings` спредом, поэтому ссылка меняется ВСЕГДА — даже когда автор выбрал
 * ту же самую тему второй раз. По ссылке история набивалась бы пустыми шагами,
 * и «Отменить действие» пришлось бы жать несколько раз подряд, ничего при этом
 * не меняя на экране.
 *
 * СРАВНЕНИЕ ИДЁТ ВГЛУБЬ (ревизия пачки 37, находка Э3). Здесь стояло
 * поверхностное сравнение с оговоркой «вложенные объекты редьюсеры кладут
 * целиком, не мутируя, — их ссылка честно говорит о правке». Ссылка честна
 * только когда объект переиспользуют, а его собирают заново: карточка галереи
 * фонов отдаёт НОВЫЙ объект с тем же значением, и повторный выбор того же
 * фона давал пустой шаг истории — «Отменить действие» приходилось жать
 * несколько раз подряд, ничего при этом не меняя на экране. Это ровно тот
 * дефект, от которого правило и заводилось, просто на уровень глубже.
 *
 * Цена честно: сравнение стоит обхода оформления. Оформление — десяток-другой
 * полей на воронку (тема, палитра, фон, логотип, набор стиля), а зовётся
 * сравнение на редьюсер оформления, не на каждый кадр. Содержимое и логику
 * оно не трогает — там по-прежнему ссылка.
 */
function valuesEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((item, i) => valuesEqual(item, b[i]));
  }
  const aObject = typeof a === 'object' && a !== null && !Array.isArray(a);
  const bObject = typeof b === 'object' && b !== null && !Array.isArray(b);
  if (!aObject || !bObject) return false;
  const aRecord = a as Record<string, unknown>;
  const bRecord = b as Record<string, unknown>;
  const aKeys = Object.keys(aRecord);
  const bKeys = Object.keys(bRecord);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every(
    (key) => key in bRecord && valuesEqual(aRecord[key], bRecord[key]),
  );
}

function settingsEqual(a: EditorState['settings'], b: EditorState['settings']) {
  if (a === b) return true;
  const aKeys = Object.keys(a ?? {});
  const bKeys = Object.keys(b ?? {});
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key) =>
    valuesEqual(
      (a as Record<string, unknown>)[key],
      (b as Record<string, unknown>)[key],
    ),
  );
}

/** Shared push-history-if-changed logic, factored out of the two wrappers below. */
export function applyHistoryIfChanged(
  state: EditorState,
  next: EditorState,
): EditorState {
  if (
    next.content === state.content &&
    next.logic === state.logic &&
    // WEB-1239 (п.6 карточки): оформление входит в отменяемое состояние
    // наравне с содержимым. До этой карточки снимок был «content + logic
    // only», и подобранную тему/палитру/логотип не возвращало ничто, кроме
    // памяти автора, — при том что на живую страницу они уезжали сразу.
    settingsEqual(next.settings, state.settings)
  ) {
    return next;
  }
  const priorSnapshot: ContentSnapshot = {
    content: state.content,
    logic: state.logic,
    settings: state.settings,
  };
  const past = [...(state.past ?? []), priorSnapshot].slice(-HISTORY_LIMIT);
  return { ...next, past, future: [] };
}

// Two separate wrappers (rather than one overloaded generic) mirror the two
// shapes case reducers take in this slice: no-payload (e.g. addScreen) and
// PayloadAction<P>. Each preserves the wrapped reducer's exact arity/type so
// the generated RTK action creator keeps requiring (or not requiring) a
// payload arg exactly as it would without the wrapper.
export function withHistory(
  reducer: (state: EditorState) => EditorState,
): (state: EditorState) => EditorState {
  return (state: EditorState) => applyHistoryIfChanged(state, reducer(state));
}

export function withHistoryPayload<P>(
  reducer: (state: EditorState, action: PayloadAction<P>) => EditorState,
): (state: EditorState, action: PayloadAction<P>) => EditorState {
  return (state: EditorState, action: PayloadAction<P>) =>
    applyHistoryIfChanged(state, reducer(state, action));
}

/**
 * WEB-624: pop the most recent snapshot off `past`, push the current
 * content/logic onto `future`, and apply the popped snapshot. No-op when
 * there is nothing to undo. Marked dirty since the resulting content
 * differs from what was last saved.
 */
export function undoState(state: EditorState): EditorState {
  const past = state.past ?? [];
  if (past.length === 0) return state;
  const previous = past[past.length - 1];
  const remainingPast = past.slice(0, -1);
  const currentSnapshot: ContentSnapshot = {
    content: state.content,
    logic: state.logic,
    settings: state.settings,
  };
  const future = [currentSnapshot, ...(state.future ?? [])].slice(
    0,
    HISTORY_LIMIT,
  );
  return {
    ...state,
    content: previous.content,
    logic: previous.logic,
    // WEB-1239: снимки, снятые ДО этой карточки, оформления не несут — у них
    // поле отсутствует, и тогда текущее оформление остаётся как есть (шаг
    // отмены не обязан ничего ломать из-за возраста снимка).
    settings: previous.settings ?? state.settings,
    past: remainingPast,
    future,
    isDirty: true,
  };
}

/**
 * WEB-624: mirror of `undo` — pop the most recent snapshot off `future`,
 * push the current content/logic onto `past`, and apply the popped
 * snapshot. No-op when there is nothing to redo.
 */
export function redoState(state: EditorState): EditorState {
  const future = state.future ?? [];
  if (future.length === 0) return state;
  const [next, ...remainingFuture] = future;
  const currentSnapshot: ContentSnapshot = {
    content: state.content,
    logic: state.logic,
    settings: state.settings,
  };
  const past = [...(state.past ?? []), currentSnapshot].slice(-HISTORY_LIMIT);
  return {
    ...state,
    content: next.content,
    logic: next.logic,
    // WEB-1239: зеркало отмены — повтор возвращает и оформление.
    settings: next.settings ?? state.settings,
    past,
    future: remainingFuture,
    isDirty: true,
  };
}
